/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What the duplicate control offers, and which option it starts on** (`P3a`).
 *
 * Separated from `CloneArtifactDialog` for the reason `provenance-presentation.ts` is separated from
 * `ProvenanceChip`: the **decisions** are testable and the rendering is not. This console has no
 * component-test setup — its vitest includes `src/**\/*.test.ts` only — so a `.tsx` test would have
 * silently matched nothing, which is worse than no test.
 *
 * So this file carries the three things that are easy to undo by accident later, and
 * `clone-options.test.ts` asserts them. **What stays unasserted is the rendering** — that both
 * options appear, that the selected one is visibly selected, that the error stays on screen. Those
 * need a person or a component harness, and that gap is real rather than covered.
 */

import type { CloneMode } from '../../types/demand-source';

export interface CloneOption {
  mode: CloneMode;
  label: string;
  /**
   * What choosing it **does**, in the operator's terms.
   *
   * #1045: naming a mechanism is not describing an effect. *"Sets a parent link"* tells an operator
   * nothing they can act on; *"later edits there reach this copy"* is the outcome they are choosing
   * between, and it is the whole reason this is one control rather than two buttons.
   */
  detail: string;
  recommended?: true;
}

export const CLONE_OPTIONS: readonly CloneOption[] = [
  {
    mode: 'extend',
    label: 'Extend the original',
    detail: 'Keeps everything you do not change in step with the original. Later edits there reach this copy.',
    recommended: true,
  },
  {
    mode: 'independent',
    label: 'Save as independent',
    detail: 'Takes a snapshot. The original can change or be deleted with no effect on this copy.',
  },
];

/**
 * The option the control starts on.
 *
 * **The recoverable one.** An extended copy can be detached later and keeps every value; an
 * independent copy cannot be retro-linked without guessing which of its values were meant to track
 * the original. *Default to the choice you can change your mind about.*
 */
export const DEFAULT_CLONE_MODE: CloneMode = 'extend';

/**
 * **What the copy *is*** — the other half of the duplicate gesture (`ST12` item 2, `#1358`).
 *
 * `TM9` is that `isTemplate` is **orthogonal** to the relation: an operator saving a template still
 * chooses extend or independent. So this is a second control beside {@link CLONE_OPTIONS}, not a
 * third member of it.
 *
 * > **Measured before collapsing anything.** `ST12` described the fix as *"one entry point, three
 * > outcomes"* — copy · variant · template. The surfaces offer **four**: template × extend and
 * > template × independent are both reachable today, and `TemplatesPage` exists because a template
 * > that extends another is the useful case. A three-member control would have deleted *template as
 * > a variant* while looking like the ruling being carried out, which is the shape `#1273` names:
 * > a design instruction says what comes **off**, never what stays. What `Q6` actually ruled is that
 * > the **entry** collapses — the list's two buttons become one — and that is what this builds.
 *
 * The words are the caller's because the dialog is type-agnostic, and *what a copy of this is* is
 * not: a demand source's instance serves requests, a rule set's does not. `null` on the prop is the
 * type with no template plane at all, which is a different statement from *not offered here*.
 */
export interface ArtifactOutcomeOption {
  /** `true` is the template. A boolean because `ClonedArtifactIdentity.asTemplate` is one. */
  asTemplate: boolean;
  label: string;
  /** What choosing it **does** — the same rule as {@link CloneOption.detail}. */
  detail: string;
}

/**
 * The demand-source words, in one place because two surfaces say them: this dialog and the wizard's
 * *Create template* confirm. *Shared logic is not shared words* (`#1187`) — the two make the same
 * decision, so they share the sentence and not only the mechanism.
 */
export const DEMAND_SOURCE_OUTCOMES: readonly ArtifactOutcomeOption[] = [
  {
    asTemplate: false,
    label: 'Demand source',
    detail: 'Serves requests. Selectable by strategies as soon as it is enabled.',
  },
  {
    asTemplate: true,
    label: 'Template',
    detail: 'A baseline others start from. Never serves a request, and never selected by a strategy.',
  },
];

/**
 * **The request strategy words** (`RQ10`, slice 7b). Offered on every strategy Duplicate, starting on
 * what the original is, so a template's Duplicate is also where a strategy is made from it.
 */
export const REQ_STRATEGY_OUTCOMES: readonly ArtifactOutcomeOption[] = [
  {
    asTemplate: false,
    label: 'Request strategy',
    detail: 'Used by the sessions that name it or are given it. Runs as soon as it is enabled.',
  },
  {
    asTemplate: true,
    label: 'Template',
    detail: 'A baseline others start from. Never used for a session.',
  },
];
